import { Search, Sun, Moon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppSettings } from "@/contexts/AppSettingsContext";
import { cn } from "@/utils";
import { NotificationsPanel } from "@/components/shared/notifications";

interface TopBarActionsProps {
	searchQuery?: string;
	onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	hideSearch?: boolean;
}

export const TopBarActions = ({
	searchQuery = "",
	onSearchChange,
	hideSearch,
}: TopBarActionsProps) => {
	const { t } = useTranslation();
	const { isDarkMode, setTheme } = useAppSettings();

	const toggleDarkMode = () => {
		setTheme(isDarkMode ? "light" : "dark");
	};

	return (
		<div className="flex items-center gap-3">
			{/* Search Box - hidden when hideSearch is true */}
			{!hideSearch && (
				<div className="relative h-8 flex items-center">
					<Search
						size={14}
						className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-text-primary"
					/>
					<input
						type="text"
						placeholder={t("nav.search") || "Search..."}
						value={searchQuery}
						onChange={onSearchChange}
						readOnly={!onSearchChange}
						className="h-full pl-8 pr-2.5 w-40 text-xs border border-border-secondary rounded-md focus:outline-none focus:ring-2 focus:ring-ring-focus focus:border-transparent bg-bg-primary shadow-sm placeholder:text-text-secondary"
					/>
				</div>
			)}

			{/* Dark Mode Toggle */}
			<button
				type="button"
				onClick={toggleDarkMode}
				className="flex items-center bg-bg-tertiary rounded-full p-1 cursor-pointer hover:bg-hover-secondary transition-colors"
				title={
					!isDarkMode
						? t("nav.darkMode") || "Dark mode"
						: t("nav.lightMode") || "Light mode"
				}
			>
				<div
					className={cn(
						"p-1.5 rounded-full transition-colors duration-200",
						!isDarkMode
							? "bg-bg-primary shadow-sm"
							: "hover:bg-hover-secondary",
					)}
				>
					<Sun
						size={14}
						className={cn(
							"transition-colors",
							!isDarkMode ? "text-text-primary" : "text-text-secondary",
						)}
					/>
				</div>
				<div
					className={cn(
						"p-1.5 rounded-full transition-colors duration-200",
						isDarkMode ? "bg-bg-primary shadow-sm" : "hover:bg-hover-secondary",
					)}
				>
					<Moon
						size={14}
						className={cn(
							"transition-colors",
							isDarkMode ? "text-text-primary" : "text-text-secondary",
						)}
					/>
				</div>
			</button>

			{/* Notifications */}
			<NotificationsPanel />
		</div>
	);
};
